import { useState, useMemo } from 'react';
import { getCompanies, getCompanyDetails } from '../../utils/companyDataLoader';
import { MarketingCosts } from './costs/MarketingCosts';
import { MarketSegments } from './marketing/MarketSegments';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ArrowUpDown } from 'lucide-react';
import { PlaceholderText } from '../ui/Placeholder';
import { reputationHistoryService } from '../../services/reputationHistoryService';
import { TabContainer, Tab } from '../ui';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface MarketViewProps {
  companyState: {
    marketShare: number;
    reputation: number;
    cash: number;
    day: number;
    month: number;
    year: number;
    dailyStats?: {
      revenue: number;
      costs: number;
      profit: number;
      unitsSold?: number;
    };
  };
  companyName: string;
  ceoName?: string;
  products: Array<{
    id: string;
    name: string;
    price: number;
    techScore: number;
    marketAppeal: number;
    releaseDate?: {
      day: number;
      month: number;
      year: number;
    };
    status: string;
    salesHistory?: {
      totalUnits: number;
      dailyAverage: number;
    };
  }>;
}

export function MarketView({ companyState, companyName, ceoName, products }: MarketViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'segments' | 'costs'>('overview');
  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'reputation';
    direction: 'asc' | 'desc';
  }>({ key: 'reputation', direction: 'desc' });

  // Get competitor companies
  const companies = getCompanies(
    { year: companyState.year },
    companyName && ceoName ? { name: companyName, ceoName } : undefined
  );

  // Update player company product count
  if (companies[0]) {
    companies[0].productCount = products.filter(p => p.status === 'production').length;
  }

  // Generate historical reputation data
  const reputationHistory = useMemo(() => {
    // console.log('[MarketView] Generating reputation history with current reputation:', companyState.reputation);

    // Get actual historical data from the service
    const historicalData = reputationHistoryService.getMonthlyHistory({
      year: companyState.year,
      month: companyState.month
    });

    // If we have no history, use current reputation for the current month
    if (historicalData.every(d => d.reputation === null)) {
      historicalData[historicalData.length - 1].reputation = companyState.reputation;
    }

    // console.log('[MarketView] Historical reputation data:', historicalData);

    return historicalData;
  }, [companyState.reputation, companyState.month, companyState.year]);

  const reputationChartData = {
    labels: reputationHistory.map(d => d.month),
    datasets: [
      {
        label: 'Company Reputation',
        data: reputationHistory.map(d => d.reputation),
        borderColor: 'rgb(99, 102, 241)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: { parsed: { y?: number } }) => {
            return `Reputation: ${context.parsed.y?.toFixed(1) || 0}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value: number | string) => `${value}%`
        }
      }
    }
  };

  // Get detailed company data with reputation
  const companiesWithReputation = useMemo(() => {
    return companies.map(company => {
      // Get the full company details including reputation
      const companyDetails = getCompanyDetails(company.id);
      const compReputation = companyDetails?.metrics?.reputation || 50; // Default to 50 if not found
      // console.log(`[MarketView] Competitor ${company.name} reputation:`, compReputation);
      return {
        ...company,
        reputation: compReputation
      };
    }).filter(c => !c.isPlayer); // Filter out player company from competitors
  }, [companies]);

  // Sort companies for reputation table
  const sortedCompanies = useMemo(() => {
    // console.log('[MarketView] Player reputation for rankings:', companyState.reputation);
    const allCompanies = [
      {
        name: companyName,
        reputation: companyState.reputation,
        isPlayer: true
      },
      ...companiesWithReputation.map(company => ({
        name: company.name,
        reputation: company.reputation,
        isPlayer: false
      }))
    ];

    return [...allCompanies].sort((a, b) => {
      const valueA = sortConfig.key === 'name' ? a.name : a.reputation;
      const valueB = sortConfig.key === 'name' ? b.name : b.reputation;
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortConfig.direction === 'asc' 
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      
      return sortConfig.direction === 'asc' 
        ? (valueA as number) - (valueB as number)
        : (valueB as number) - (valueA as number);
    });
  }, [companiesWithReputation, companyName, companyState.reputation, sortConfig]);

  const handleSort = (key: 'name' | 'reputation') => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'segments', label: 'Market Segments' },
    { id: 'costs', label: 'Costs' },
  ];

  return (
    <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}>
      <div className="space-y-6">
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reputation Graph */}
          <div className="bg-theme-bg-content rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Reputation Over Time</h3>
            <div className="h-64">
              <Line data={reputationChartData} options={chartOptions} />
            </div>
          </div>

          {/* Companies Reputation Table */}
          <div className="bg-theme-bg-content rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-theme-text-primary">Company Reputation Rankings</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-1 font-medium text-theme-text-secondary hover:text-theme-text-primary"
                        >
                          Company
                          <ArrowUpDown className="w-4 h-4" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <button
                          onClick={() => handleSort('reputation')}
                          className="flex items-center gap-1 font-medium text-theme-text-secondary hover:text-theme-text-primary"
                        >
                          Reputation
                          <ArrowUpDown className="w-4 h-4" />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCompanies.map((company) => (
                      <tr
                        key={company.name}
                        className={`border-b border-theme-border hover:bg-theme-bg-secondary ${
                          company.isPlayer ? 'bg-theme-accent/10' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-theme-text-primary">
                          <span className={company.isPlayer ? 'font-semibold text-theme-accent' : ''}>
                            {company.name}
                          </span>
                          {company.isPlayer && (
                            <span className="ml-2 text-xs text-theme-accent bg-theme-accent/20 px-2 py-1 rounded">
                              You
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-theme-bg-tertiary rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  company.isPlayer ? 'bg-theme-accent' : 'bg-theme-text-muted'
                                }`}
                                style={{ width: `${company.reputation}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-theme-text-secondary">
                              {company.reputation.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'campaigns' && (
        <div className="flex items-center justify-center h-full text-theme-text-muted">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2 text-theme-text-primary">Marketing Campaigns</h2>
            <PlaceholderText text="Marketing campaigns and advertising management coming soon." />
          </div>
        </div>
      )}

      {activeTab === 'segments' && (
        <MarketSegments 
          products={products}
          companyName={companyName}
        />
      )}

      {activeTab === 'costs' && (
        <MarketingCosts
          products={products}
          reputation={companyState.reputation}
          monthlyRevenue={(companyState.dailyStats?.revenue || 0) * 30}
          companySize={
            companyState.cash < 100000 ? 'startup' :
            companyState.cash < 1000000 ? 'small' :
            companyState.cash < 10000000 ? 'medium' : 'large'
          }
        />
      )}
      </div>
    </TabContainer>
  );
}